import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {App, Button, Descriptions, Drawer, Table, Tag} from 'antd';
import {useI18n} from '../../../i18n/useI18n';
import {getDcaMarketCandles, getDcaPlanCost, listDcaPlanExecutions} from '../../../services/dcaService';
import LightweightCandlestickChart, {KLineBar} from '../../../components/charts/LightweightCandlestickChart';
import type {DcaIndicatorSeries, DcaMarketCandle, DcaPlan, DcaPlanCost, DcaPlanExecution} from '../../../types/dca';
import {buildConditionsText, fmtTime, parseConditionsJson} from './dcaConditionUtil';
import {buildHighlightSegments, buildIndicatorLegend, toChartIndicators} from './dcaIndicatorUtil';
import DcaIndicatorLegend from './DcaIndicatorLegend';

/** 计划状态徽标配色 */
const STATUS_META: Record<string, { color: string }> = {
    ACTIVE: {color: 'green'},
    PAUSED: {color: 'orange'},
    STOPPED: {color: 'default'},
    COMPLETED: {color: 'blue'},
};

/** 执行状态徽标 */
const EXEC_META: Record<string, { color: string }> = {
    EXECUTING: {color: 'processing'},
    SUCCESS: {color: 'green'},
    FAILED: {color: 'red'},
};

/** 触发快照条目（后端 triggerSnapshotJson 的 items 元素） */
interface SnapshotItem {
    text?: string;
    matched?: boolean;
}

/** 触发快照 JSON 结构 */
interface Snapshot {
    matched?: boolean;
    op?: string;
    items?: SnapshotItem[];
}

interface Props {
    plan: DcaPlan | null;
    onClose: () => void;
    /** 暂停/恢复/停止操作（确认弹窗由父页处理） */
    onAction: (action: 'pause' | 'resume' | 'stop', plan: DcaPlan) => void;
    onEdit: (plan: DcaPlan) => void;
}

const parseSnapshot = (json?: string): Snapshot | null => {
    if (null == json || '' === json) {
        return null;
    }
    try {
        return JSON.parse(json) as Snapshot;
    } catch {
        return null;
    }
};

/**
 * 计划详情抽屉
 * 状态行 + 触发条件展示 + 执行流水（成败/量价/金额/原因，快照展开）+ 操作按钮。
 */
const DcaPlanDetailDrawer: React.FC<Props> = ({plan, onClose, onAction, onEdit}) => {
    const {t} = useI18n();
    const {message} = App.useApp();
    const [executions, setExecutions] = useState<DcaPlanExecution[]>([]);
    const [execLoading, setExecLoading] = useState(false);
    const [cost, setCost] = useState<DcaPlanCost | null>(null);
    const [candles, setCandles] = useState<DcaMarketCandle[]>([]);
    const [candleIndicators, setCandleIndicators] = useState<Record<string, DcaIndicatorSeries[]> | null>(null);
    /** 用户点击图例隐藏的指标（visibleKey → true） */
    const [hiddenIndicators, setHiddenIndicators] = useState<Record<string, boolean>>({});
    const [chartLoading, setChartLoading] = useState(false);

    const loadExecutions = useCallback(async (planId: number) => {
        setExecLoading(true);
        try {
            setExecutions(await listDcaPlanExecutions(planId));
        } catch (e: any) {
            message.error(e?.message ?? t('dca.detail.loadExecError'));
            setExecutions([]);
        } finally {
            setExecLoading(false);
        }
    }, [message, t]);

    const loadCost = useCallback(async (planId: number) => {
        try {
            setCost(await getDcaPlanCost(planId));
        } catch (e: any) {
            message.error(e?.message ?? t('dca.cost.loadError'));
            setCost(null);
        }
    }, [message, t]);

    useEffect(() => {
        if (null != plan) {
            void loadExecutions(plan.id);
            void loadCost(plan.id);
        } else {
            setExecutions([]);
            setCost(null);
        }
    }, [plan, loadExecutions, loadCost]);

    // 计划K线 + 条件指标/高亮（打开详情时按计划品种/周期/条件加载最近K线）
    useEffect(() => {
        if (null == plan) {
            setCandles([]);
            setCandleIndicators(null);
            setChartLoading(false);
            return;
        }
        // 切换计划时重置图例显隐（新计划的指标集合可能不同）
        setHiddenIndicators({});
        let cancelled = false;
        setChartLoading(true);
        void getDcaMarketCandles({
            symbol: plan.symbol,
            timeframe: plan.timeframe,
            conditionsJson: plan.conditionsJson,
        }).then((data) => {
            if (!cancelled) {
                setCandles(data?.candles ?? []);
                setCandleIndicators(data?.indicators ?? null);
            }
        }).catch(() => {
            if (!cancelled) {
                setCandles([]);
                setCandleIndicators(null);
            }
        }).finally(() => {
            if (!cancelled) {
                setChartLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [plan]);

    const parsedConditions = useMemo(() => parseConditionsJson(plan?.conditionsJson), [plan?.conditionsJson]);
    const bars = useMemo<KLineBar[]>(() => candles.map((c) => ({
        time: c.time,
        open: Number(c.open),
        high: Number(c.high),
        low: Number(c.low),
        close: Number(c.close),
        volume: Number(c.volume ?? 0),
    })), [candles]);
    const chartIndicatorPayload = useMemo(
        () => toChartIndicators({candles, indicators: candleIndicators}),
        [candleIndicators],
    );
    const indicatorLegend = useMemo(() => buildIndicatorLegend(candleIndicators), [candleIndicators]);
    const hiddenIndicatorKeys = useMemo(
        () => new Set(Object.keys(hiddenIndicators).filter((k) => hiddenIndicators[k])),
        [hiddenIndicators],
    );
    const effectiveVisibleIndicators = useMemo(() => {
        const base = chartIndicatorPayload.visibleIndicators;
        const out: Record<string, boolean> = {};
        Object.keys(base).forEach((key) => {
            out[key] = !hiddenIndicators[key];
        });
        return out;
    }, [chartIndicatorPayload.visibleIndicators, hiddenIndicators]);
    const toggleIndicator = useCallback((visibleKey: string) => {
        setHiddenIndicators((prev) => ({...prev, [visibleKey]: !prev[visibleKey]}));
    }, []);
    const highlightSegments = useMemo(
        () => buildHighlightSegments(parsedConditions, candles, candleIndicators, hiddenIndicatorKeys),
        [parsedConditions, candles, candleIndicators, hiddenIndicatorKeys],
    );

    const canOperate = null != plan && ('ACTIVE' === plan.status || 'PAUSED' === plan.status);

    const execColumns = [
        {
            title: t('dca.detail.time'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (v?: string) => fmtTime(v),
        },
        {
            title: t('dca.detail.status'),
            dataIndex: 'execStatus',
            key: 'execStatus',
            width: 90,
            render: (v: string) => (
                <Tag color={EXEC_META[v]?.color ?? 'default'}>{t(`dca.detail.exec.${v}`)}</Tag>
            ),
        },
        {
            title: t('dca.detail.amount'),
            dataIndex: 'amountUsdt',
            key: 'amountUsdt',
            width: 100,
            render: (v?: number) => null == v ? '--' : v,
        },
        {
            title: t('dca.detail.fill'),
            key: 'fill',
            width: 170,
            render: (_: unknown, row: DcaPlanExecution) => (
                <span>
                    {null == row.filledQty || null == row.plannedQty
                        ? '--'
                        : `${row.filledQty} / ${row.plannedQty}${null != row.avgPx ? ` @ ${row.avgPx}` : ''}`}
                    {1 === row.dryRun ?
                        <Tag className="dca-dryrun-tag" color="blue">{t('dca.detail.dryRun')}</Tag> : null}
                </span>
            ),
        },
        {
            title: t('dca.detail.reason'),
            dataIndex: 'errorMsg',
            key: 'errorMsg',
            ellipsis: true,
            render: (v?: string) => null == v || '' === v ? '--' : v,
        },
    ];

    return (
        <Drawer
            open={null != plan}
            title={t('dca.detail.title')}
            width={720}
            onClose={onClose}
            footer={
                null != plan && canOperate ? (
                    <div className="dca-drawer-footer">
                        <Button onClick={() => onEdit(plan)}>{t('dca.plan.edit')}</Button>
                        {'ACTIVE' === plan.status ? (
                            <Button onClick={() => onAction('pause', plan)}>{t('dca.plan.pause')}</Button>
                        ) : (
                            <Button type="primary"
                                    onClick={() => onAction('resume', plan)}>{t('dca.plan.resume')}</Button>
                        )}
                        <Button danger onClick={() => onAction('stop', plan)}>{t('dca.plan.stop')}</Button>
                    </div>
                ) : null
            }
        >
            {null != plan ? (
                <div className="dca-detail">
                    <Descriptions title={t('dca.detail.basic')} column={2} size="small" bordered>
                        <Descriptions.Item label={t('dca.form.planName')} span={2}>{plan.planName}</Descriptions.Item>
                        <Descriptions.Item label={t('dca.form.symbol')}>{plan.symbol}</Descriptions.Item>
                        <Descriptions.Item label={t('dca.detail.status')}>
                            <Tag color={STATUS_META[plan.status]?.color ?? 'default'}>
                                {t(`dca.plan.status.${plan.status}`)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label={t('dca.form.lifecycle')}>
                            {t(`dca.plan.lifecycle.${plan.lifecycle}`)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('dca.form.timeframe')}>{plan.timeframe}</Descriptions.Item>
                        <Descriptions.Item label={t('dca.form.monitorFrequency')}>
                            {plan.monitorFrequency} {t('dca.form.monitorFrequencyUnit')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('dca.plan.buyProgress')}>
                            {plan.boughtCount} / {plan.maxBuyCount}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('dca.form.dailyMaxTriggers')}>
                            {null == plan.dailyMaxTriggers ? t('dca.form.unlimited') : plan.dailyMaxTriggers}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('dca.plan.signal')}>
                            <Tag color={1 === plan.lastSignalState ? 'green' : 'default'}>
                                {t(`dca.plan.signal.${plan.lastSignalState}`)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label={t('dca.plan.lastChecked')}>
                            {fmtTime(plan.lastCheckedAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('dca.detail.riskConfirmedAt')}>
                            {fmtTime(plan.riskConfirmedAt)}
                        </Descriptions.Item>
                        {null != plan.blockReason ? (
                            <Descriptions.Item label={t('dca.detail.blockReason')} span={2}>
                                <Tag color="red">{t(`dca.plan.block.${plan.blockReason}`)}</Tag>
                                <span className="dca-card-label" style={{marginLeft: 8}}>
                                    {fmtTime(plan.blockedAt)}
                                </span>
                            </Descriptions.Item>
                        ) : null}
                        <Descriptions.Item
                            label={t('dca.detail.createdAt')}>{fmtTime(plan.createdAt)}</Descriptions.Item>
                        <Descriptions.Item
                            label={t('dca.detail.updatedAt')}>{fmtTime(plan.updatedAt)}</Descriptions.Item>
                    </Descriptions>

                    <div className="dca-detail-section">
                        <div className="dca-detail-section-title">{t('dca.cost.title')}</div>
                        <CostPanel cost={cost}/>
                    </div>

                    <div className="dca-detail-section">
                        <div className="dca-detail-section-title">{t('dca.detail.conditions')}</div>
                        <div
                            className="dca-preview">{buildConditionsText(parseConditionsJson(plan.conditionsJson), t)}</div>
                    </div>

                    <div className="dca-detail-section">
                        <div className="dca-detail-section-title">{t('dca.calc.chart')}</div>
                        {0 === candles.length ? (
                            <div className="dca-calc-chart-empty">
                                {chartLoading ? t('dca.calc.chartLoading') : t('dca.calc.chartEmpty')}
                            </div>
                        ) : (
                            <>
                                <DcaIndicatorLegend
                                    items={indicatorLegend}
                                    hidden={hiddenIndicators}
                                    onToggle={toggleIndicator}
                                />
                                <LightweightCandlestickChart
                                    data={bars}
                                    indicators={chartIndicatorPayload.indicators}
                                    visibleIndicators={effectiveVisibleIndicators}
                                    highlightSegments={highlightSegments}
                                    height={300}
                                    maxVisibleBars={240}
                                />
                            </>
                        )}
                    </div>

                    <div className="dca-detail-section">
                        <div className="dca-detail-section-title">{t('dca.detail.executions')}</div>
                        <Table<DcaPlanExecution>
                            rowKey="id"
                            size="small"
                            loading={execLoading}
                            columns={execColumns}
                            dataSource={executions}
                            pagination={{pageSize: 10, hideOnSinglePage: true}}
                            locale={{emptyText: t('dca.detail.execEmpty')}}
                            expandable={{
                                expandedRowRender: (row) => <SnapshotView snapshotJson={row.triggerSnapshotJson}/>,
                                rowExpandable: (row) => null != row.triggerSnapshotJson && '' !== row.triggerSnapshotJson,
                            }}
                        />
                    </div>
                </div>
            ) : null}
        </Drawer>
    );
};

/** 数值格式化（null/undefined → --；其余去掉多余尾零） */
const fmtNum = (v?: number | null): string => null == v ? '--' : String(Number(v.toFixed(8)));

/** P1 成本面板：累计买入量/金额/均价/现价/浮盈亏（正绿负红，与项目涨跌口径一致） */
const CostPanel: React.FC<{ cost: DcaPlanCost | null }> = ({cost}) => {
    const {t} = useI18n();
    const pnl = cost?.unrealizedPnl;
    const pnlClass = null == pnl ? '' : (pnl >= 0 ? 'dca-cost-pos' : 'dca-cost-neg');
    return (
        <div className="dca-cost">
            <div className="dca-cost-item">
                <span className="dca-cost-label">{t('dca.cost.quantity')}</span>
                <span>{fmtNum(cost?.totalBuyQuantity)}</span>
            </div>
            <div className="dca-cost-item">
                <span className="dca-cost-label">{t('dca.cost.amount')}</span>
                <span>{fmtNum(cost?.totalBuyAmount)}</span>
            </div>
            <div className="dca-cost-item">
                <span className="dca-cost-label">{t('dca.cost.avgCost')}</span>
                <span>{fmtNum(cost?.avgCost)}</span>
            </div>
            <div className="dca-cost-item">
                <span className="dca-cost-label">{t('dca.cost.currentPrice')}</span>
                <span>{fmtNum(cost?.currentPrice)}</span>
            </div>
            <div className="dca-cost-item">
                <span className="dca-cost-label">{t('dca.cost.pnl')}</span>
                <span className={pnlClass}>
                    {null == pnl ? '--' : `${pnl >= 0 ? '+' : ''}${fmtNum(pnl)}`}
                </span>
            </div>
        </div>
    );
};

/** 执行流水行的触发快照展开内容（指标值+文案，matched 着色） */
const SnapshotView: React.FC<{ snapshotJson?: string }> = ({snapshotJson}) => {
    const {t} = useI18n();
    const snapshot = parseSnapshot(snapshotJson);
    if (null == snapshot || null == snapshot.items || 0 === snapshot.items.length) {
        return <div className="dca-snapshot-empty">{t('dca.form.previewEmpty')}</div>;
    }
    return (
        <div className="dca-snapshot">
            <div className="dca-snapshot-op">
                {'AND' === snapshot.op ? t('dca.preview.and') : t('dca.preview.or')}
            </div>
            {snapshot.items.map((item, idx) => (
                <div key={idx} className={`dca-snapshot-item ${item.matched ? 'matched' : ''}`}>
                    <span className="dca-snapshot-dot">{item.matched ? '✓' : '✗'}</span>
                    <span>{item.text ?? '--'}</span>
                </div>
            ))}
        </div>
    );
};

export default DcaPlanDetailDrawer;
